// WbsTreeTable.tsx — 최종본 (sessionStorage 기반 스크롤 유지)

import React, { useEffect, useState, useMemo, useCallback, useRef, useLayoutEffect } from 'react';

import { parseISO, toProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import type { DraftItem, FlatRow, KeyId } from '../types.local';
import { keyOf } from '../types.local';
import { useColumnResize } from 'src/hooks/useColumnResize';

type TreeIndex = {
    childrenMap: Record<string, string[]>;
    parentMap: Record<string, string | null>;
    parentsWithChildren: Set<string>;
};

// 파란 계열 팔레트 (원하는 색상으로 바꿔도 됨)
const SIBLING_BLUE_PALETTE = [
    '#1d4ed8', // blue-700
    '#2563eb', // blue-600
    '#3b82f6', // blue-500
    '#60a5fa', // blue-400
    '#93c5fd', // blue-300
    '#0ea5e9', // sky-500
    '#38bdf8', // sky-400
    '#7dd3fc', // sky-300
    '#0284c7', // sky-600
    '#0369a1', // sky-700
    '#1e40af', // indigo-800
    '#3730a3', // indigo-700
];

// ── Level-based color palette (레벨별 다른 컬러 계열, 차분한 톤) ──
// const LEVEL_PALETTE: string[] = [
//   '#92d31aff', // level 0: indigo-700  (딥 인디고)
//   '#d8af1dff', // level 1: blue-700    (네이비 블루)
//   '#76caf1ff', // level 2: sky-500     (스카이)
//   '#0d9488', // level 3: teal-600    (틸)
//   '#16a34a', // level 4: green-600   (그린)
//   '#d97706', // level 5: amber-600   (앰버)
//   '#ea580c', // level 6: orange-600  (오렌지)
//   '#7c3aed', // level 7: violet-600  (바이올렛)
// ];

// ── Level-based color palette (lime: 깊어질수록 짙게) ──
// const LEVEL_PALETTE: string[] = [
//   '#d9f99d', // level 0 : lime-200  (가장 연함)
//   '#bef264', // level 1 : lime-300
//   '#a3e635', // level 2 : lime-400
//   '#84cc16', // level 3 : lime-500
//   '#65a30d', // level 4 : lime-600
//   '#4d7c0f', // level 5 : lime-700
//   '#3f6212', // level 6 : lime-800
//   '#365314', // level 7 : lime-900  (가장 진함)
// ];

// ── Level-based color palette (요트블루: 깊어질수록 짙게) ──
const LEVEL_PALETTE: string[] = [
    '#e0f2fe', // level 0 : sky-100  (가장 연함, 하늘빛)
    '#bae6fd', // level 1 : sky-200
    '#7dd3fc', // level 2 : sky-300
    '#38bdf8', // level 3 : sky-400
    '#0ea5e9', // level 4 : sky-500
    '#0284c7', // level 5 : sky-600
    '#0369a1', // level 6 : sky-700
    '#075985', // level 7 : sky-800 (가장 진함, 요트블루 바다색)
];

const barColorByLevel = (depth: number | undefined) => {
    const d = Math.max(0, Math.min(LEVEL_PALETTE.length - 1, depth ?? 0));
    return LEVEL_PALETTE[d];
};

// depth 기반으로 parent/children 맵 구성
// function buildTreeIndex(rows: FlatRow[]): TreeIndex {
//     const childrenMap: Record<string, string[]> = {};
//     const parentMap: Record<string, string | null> = {};
//     const parentsWithChildren = new Set<string>();

//     const stack: Array<{ depth: number; nodeId: string }> = [];
//     for (const r of rows) {
//         const id = r.nodeId!;
//         const depth = r.depth ?? 0;

//         // 현재 depth에 맞춰 스택 정리
//         while (stack.length && stack[stack.length - 1].depth >= depth) stack.pop();

//         const parent = stack.length ? stack[stack.length - 1].nodeId : null;
//         parentMap[id] = parent;

//         if (parent) {
//             if (!childrenMap[parent]) childrenMap[parent] = [];
//             childrenMap[parent].push(id);
//             parentsWithChildren.add(parent);
//         }

//         stack.push({ depth, nodeId: id });
//     }

//     return { childrenMap, parentMap, parentsWithChildren };
// }
// parentId 기반으로 parent/children 맵 구성
function buildTreeIndex(rows: FlatRow[]): TreeIndex {
    const childrenMap: Record<string, string[]> = {};
    const parentMap: Record<string, string | null> = {};
    const parentsWithChildren = new Set<string>();

    for (const r of rows) {
        const id = r.nodeId!;
        const parent = (r.parentNodeId ?? null) as string | null;

        parentMap[id] = parent;

        if (parent) {
            if (!childrenMap[parent]) childrenMap[parent] = [];
            childrenMap[parent].push(id);
            parentsWithChildren.add(parent);
        }
    }

    return { childrenMap, parentMap, parentsWithChildren };
}

function computeVisibleRows(rows: FlatRow[], expanded: Set<string>, idx: TreeIndex): FlatRow[] {
    const idToRow = new Map<string, FlatRow>(rows.map((r) => [r.nodeId!, r]));
    const roots = rows.filter((r) => idx.parentMap[r.nodeId!] == null);

    const out: FlatRow[] = [];
    const walk = (id: string) => {
        const row = idToRow.get(id);
        if (!row) return;
        out.push(row);
        const kids = idx.childrenMap[id] || [];
        if (!expanded.has(id)) return; // 부모가 접혀있으면 자식 미탐색
        for (const kid of kids) walk(kid);
    };

    for (const r of roots) walk(r.nodeId!);
    return out;
}

export default function WbsTreeTable({
    projectId,
    rows,
    selectedKey,
    onSelect,
    onMoveUp,
    onMoveDown,
    onOnlineNameChange,
    onChange,
    // 추가
    showHierarchyForDraft,
    changeKindMap,
    onSoftDelete,
    onUndoDelete,
    previewOn,
    previewCodeMap,
    busyKey,
    scheduleMode,
}: {
    projectId: string;
    rows: FlatRow[];
    selectedKey: KeyId | null;
    onSelect: (key: KeyId) => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onOnlineNameChange: (row: DraftItem, nextName: string) => void;
    onChange: (nodeId: string, patch: Partial<DraftItem>) => void;
    changeKindMap: Record<string, 'ADDED' | 'MODIFIED' | 'DELETED' | null>;
    showHierarchyForDraft: boolean;
    onSoftDelete: (row: DraftItem) => void;
    onUndoDelete: (row: DraftItem) => void;
    previewOn: boolean;
    previewCodeMap: Record<string, string>;
    busyKey: string | null;
    scheduleMode: boolean;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';
    // 세션 단위 스크롤 저장 키
    const LS_SCROLL = `wbs:scroll:${projectId}`;
    // 펼침 상태 저장 키
    const LS_EXPANDED = `wbs:expanded:${projectId}`;

    // name 컬럼폭 드래그 조정
    const nameCol = useColumnResize('name', 320, 240, 720);
    // scheduleBar 컬럼폭 드래그 조정
    const scheduleBarCol = useColumnResize('scheduleBar', 240, 240, 1600);

    // 트리 인덱스
    const treeIndex = useMemo(() => buildTreeIndex(rows), [rows]);

    // 펼침 상태 저장/복원
    const [expanded, setExpanded] = useState<Set<string>>(() => {
        try {
            const raw = localStorage.getItem(LS_EXPANDED);
            if (raw) return new Set<string>(JSON.parse(raw));
        } catch {}
        return new Set<string>(Array.from(buildTreeIndex(rows).parentsWithChildren.values()));
    });

    useEffect(() => {
        try {
            localStorage.setItem(LS_EXPANDED, JSON.stringify(Array.from(expanded)));
        } catch {}
    }, [expanded, LS_EXPANDED]);

    useEffect(() => {
        setExpanded((prev) => {
            // 기존에 열어둔 것 중, 더 이상 존재하지 않는 id만 제거
            const next = new Set<string>();
            for (const id of prev) {
                if (treeIndex.parentMap[id] !== undefined) next.add(id);
            }
            return next;
        });
    }, [treeIndex]);

    const visibleRows = useMemo(() => computeVisibleRows(rows, expanded, treeIndex), [rows, expanded, treeIndex]);

    // ─────────────────────────────────────────────────────────────
    // ✅ Validation warnings (weight / schedule) + tooltips
    // - weight mismatch: parent + children highlight
    // - schedule out-of-range: child highlight (start/finish cells)
    // ─────────────────────────────────────────────────────────────
    const { weightWarnSet, scheduleWarnSet, weightTooltipByNode, scheduleTooltipByNode } = useMemo(() => {
        const weightWarnSet = new Set<string>();
        const scheduleWarnSet = new Set<string>();

        const weightTooltipByNode: Record<string, string> = {};
        const scheduleTooltipByNode: Record<string, string> = {};

        // nodeId -> row map (현재 rows는 화면에서 쓰는 “현재 상태(드래프트/퍼블리시 반영)” 1개씩)
        const byNodeId = new Map<string, FlatRow>();
        for (const r of rows) {
            if (r?.nodeId) byNodeId.set(r.nodeId, r);
        }

        const fmtNum = (n: number) => Number(n).toFixed(4);
        const toDateOnly = (v?: string | null) => (v ? toProjectDateOnly(v, zone) : null); // YYYY-MM-DD

        // ── (1) weight mismatch: parent vs sum(children)
        // 기준:
        // - 자식 weight 중 null 섞임/부모 weight null인데 자식 weight 있음/부모 weight 있는데 자식 null 있음 -> 경고
        // - 모두 숫자면 sum(children) !== parent.weight (tolerance) -> 경고
        const EPS = 0.0001;

        for (const parentId of treeIndex.parentsWithChildren) {
            const parent = byNodeId.get(parentId);
            if (!parent || parent.isDeleted) continue;

            const childIds = treeIndex.childrenMap[parentId] ?? [];
            const children = childIds.map((cid) => byNodeId.get(cid)).filter((x): x is FlatRow => !!x && !x.isDeleted);

            if (children.length === 0) continue;

            // const pW = parent.weight;
            // const childWeights = children.map((c) => c.weight);
            // number | null 로 정규화
            const childWeights = children.map((c) => {
                if (c.weight == null) return null;
                const n = typeof c.weight === 'number' ? c.weight : Number(c.weight);
                return Number.isFinite(n) ? n : null;
            });

            // parent weight도 number로 정규화
            const pW =
                parent.weight == null
                    ? null
                    : typeof parent.weight === 'number'
                      ? parent.weight
                      : Number(parent.weight);

            const hasChildWeight = childWeights.some((w) => w != null);
            const hasChildNull = childWeights.some((w) => w == null);

            let mismatch = false;

            if (pW == null && hasChildWeight) mismatch = true;
            if (pW != null && hasChildNull) mismatch = true;

            // 모두 값이 있으면 합 비교
            const numericChildren = childWeights.filter((w): w is number => w != null);

            if (!mismatch && pW != null && numericChildren.length === children.length) {
                const sum = numericChildren.reduce((acc, w) => acc + w, 0);

                if (Math.abs(sum - pW) > EPS) mismatch = true;

                if (mismatch) {
                    const msg = `Weight 불일치: parent=${fmtNum(pW)}, sum(children)=${fmtNum(sum)}, diff=${fmtNum(sum - pW)}`;
                    weightTooltipByNode[parentId] = msg;
                    for (const c of children) weightTooltipByNode[c.nodeId!] = msg;
                }
            } else if (mismatch) {
                const msg = `Weight 불일치: parent=${pW == null ? 'null' : fmtNum(pW)}, children=[${children
                    .map((c) => (c.weight == null ? 'null' : fmtNum(Number(c.weight))))
                    .join(', ')}]`;
                weightTooltipByNode[parentId] = msg;
                for (const c of children) weightTooltipByNode[c.nodeId!] = msg;
            }

            if (mismatch) {
                weightWarnSet.add(parentId);
                for (const c of children) weightWarnSet.add(c.nodeId!);
            }
        }

        // ── (2) schedule out-of-range: child vs parent range
        // 조건: parent start/finish 있고, child start/finish 있을 때만 비교
        // childStart < parentStart OR childFinish > parentFinish -> child 경고
        for (const [childId, child] of byNodeId.entries()) {
            if (!child || child.isDeleted) continue;

            const parentId = treeIndex.parentMap[childId];
            if (!parentId) continue;

            const parent = byNodeId.get(parentId);
            if (!parent || parent.isDeleted) continue;

            const pStart = toDateOnly(parent.plannedStart);
            const pFinish = toDateOnly(parent.plannedFinish);
            const cStart = toDateOnly(child.plannedStart);
            const cFinish = toDateOnly(child.plannedFinish);

            if (!pStart || !pFinish || !cStart || !cFinish) continue;

            const outStart = cStart < pStart;
            const outFinish = cFinish > pFinish;

            if (outStart || outFinish) {
                scheduleWarnSet.add(childId);

                const reasons: string[] = [];
                if (outStart) reasons.push(`start(${cStart}) < parentStart(${pStart})`);
                if (outFinish) reasons.push(`finish(${cFinish}) > parentFinish(${pFinish})`);

                scheduleTooltipByNode[childId] = `부모 일정 범위 벗어남: ${reasons.join(', ')}`;
            }
        }

        return { weightWarnSet, scheduleWarnSet, weightTooltipByNode, scheduleTooltipByNode };
    }, [rows, treeIndex, zone]);

    const allExpanded = useMemo(() => {
        if (treeIndex.parentsWithChildren.size === 0) return false;
        for (const id of treeIndex.parentsWithChildren) {
            if (!expanded.has(id)) return false;
        }
        return true;
    }, [expanded, treeIndex.parentsWithChildren]);

    const toggleExpandAll = useCallback(() => {
        setExpanded((prev) => {
            const shouldCollapse = Array.from(treeIndex.parentsWithChildren).every((id) => prev.has(id));
            return shouldCollapse ? new Set() : new Set(treeIndex.parentsWithChildren);
        });
    }, [treeIndex.parentsWithChildren]);

    const toggleExpand = useCallback((nodeId?: string) => {
        if (!nodeId) return;
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
            return next;
        });
    }, []);

    const expandAll = useCallback(() => {
        setExpanded(new Set(treeIndex.parentsWithChildren));
    }, [treeIndex.parentsWithChildren]);

    const collapseAll = useCallback(() => {
        setExpanded(new Set());
    }, []);

    // 스크롤 컨테이너 & 행 refs
    const containerRef = useRef<HTMLDivElement>(null);
    const rowRefs = useRef(new Map<string, HTMLTableRowElement>());

    // 스크롤 복원용: 1회성 저장값(클릭/프로그램 선택 변경 직전), 상시 기록값, rAF 디바운스 id
    const savedScrollTopRef = useRef<number | null>(null);
    const lastScrollTopRef = useRef<number>(0);

    // 이 코드 없애면 하위레벨에 추가할 때 펼쳐지지 않음
    // 선택 변경 시 조상 자동 펼침(하위 레벨 선택하면 상위가 접혀있어도 보이도록) — 복원 이후 한 텀 지연
    useEffect(() => {
        if (!selectedKey) return;

        const row = rows.find((r) => keyOf(r) === selectedKey);
        if (!row || !row.nodeId) return;

        const ancestors: string[] = [];
        let cur: string | null | undefined = treeIndex.parentMap[row.nodeId];
        while (cur) {
            ancestors.push(cur);
            cur = treeIndex.parentMap[cur];
        }
        if (!ancestors.length) return;

        // 복원(rAF*2) 뒤 한 텀 더 미뤄서 펼침 적용 → 위치 변동 체감 최소화
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setExpanded((prev) => {
                    const next = new Set(prev);
                    for (const id of ancestors) next.add(id);
                    return next;
                });
            });
        });
    }, [selectedKey, rows, treeIndex.parentMap]);

    // 리렌더/리마운트 후 스크롤 복원
    // 이 부분없으면 스크롤 튕김 발생
    useLayoutEffect(() => {
        const scroller = containerRef.current;
        if (!scroller) return;

        // 1순위: 클릭/프로그램 전 저장값 → 2순위: sessionStorage → 3순위: 런타임 기록값
        let y: number | null = savedScrollTopRef.current;
        if (y == null) {
            const raw = sessionStorage.getItem(LS_SCROLL);
            if (raw != null) y = Number(raw);
        }
        if (y == null) y = lastScrollTopRef.current;

        const apply = () => {
            const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
            scroller.scrollTop = Math.min(Math.max(0, y as number), max);
        };

        // 레이아웃/앵커링 이후 프레임에 적용 (double rAF)
        requestAnimationFrame(() => requestAnimationFrame(apply));

        // 1회성 저장값은 소모
        savedScrollTopRef.current = null;
    }, [visibleRows, selectedKey, LS_SCROLL]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (!e.altKey) return;
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                e.stopPropagation();
                onMoveUp();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopPropagation();
                onMoveDown();
            }
        },
        [onMoveUp, onMoveDown],
    );

    // useEffect(() => {
    //     containerRef.current?.focus?.({ preventScroll: true } as any);
    // }, [selectedKey]);

    // ── 색상 유틸: 부모-자식 한 계열(hue) + 자식일수록 더 밝게 ─────────────────
    function hashString(s: string) {
        let h = 0;
        for (let i = 0; i < s.length; i++) {
            h = (h << 5) - h + s.charCodeAt(i);
            h |= 0;
        }
        return Math.abs(h);
    }

    // nodeId의 최상위 조상(root) 찾기 → 같은 트리/패밀리는 같은 hue
    const getRootAncestor = (nodeId: string, parentMap: Record<string, string | null>) => {
        let cur: string | null | undefined = nodeId;
        let last = nodeId;
        while (cur) {
            last = cur;
            cur = parentMap[cur];
        }
        return last;
    };

    // hue는 파란 계열(예: 200~240°)에서 고정/선택, depth로 명도 보정
    const barColorFor = (nodeId: string, depth: number, parentMap: Record<string, string | null>) => {
        const root = getRootAncestor(nodeId, parentMap);
        const hue = 200 + (hashString(root) % 40); // 200~239 사이: 파란 계열 다양화
        const isParent = (id: string) => !!(id && treeIndex.childrenMap[id]?.length);

        // 채도/명도 기본값: 부모가 더 진하고, 자식일수록 더 밝게 (최대 한도 clamp)
        const s = 70; // 채도
        const baseL = isParent(nodeId) ? 50 : 58; // 부모는 50%, 자식은 기본 58%
        const l = Math.min(80, baseL + Math.min(depth, 4) * 4); // 깊어질수록 +명도

        return `hsl(${hue} ${s}% ${l}%)`;
    };

    // ScheduleMode에서 사용
    // 1) 전체 기간 계산
    const scheduleRange = useMemo(() => {
        if (!scheduleMode) return null;

        const dates = rows
            .map((r) => ({
                // s: r.plannedStart ? new Date(r.plannedStart) : null,
                s: parseISO(r.plannedStart),
                // f: r.plannedFinish ? new Date(r.plannedFinish) : null,
                f: parseISO(r.plannedFinish),
            }))
            .filter((x) => x.s && x.f) as { s: Date; f: Date }[];

        if (!dates.length) return null;

        const min = new Date(Math.min(...dates.map((d) => d.s.getTime())));
        const max = new Date(Math.max(...dates.map((d) => d.f.getTime())));

        const totalDays = Math.max(1, Math.round((max.getTime() - min.getTime()) / (1000 * 60 * 60 * 24)) + 1);

        return { min, max, totalDays };
    }, [rows, scheduleMode]);

    return (
        <div
            ref={containerRef} // 이걸 없애면 스크롤 튕김 현상 발생함
            className="h-full overflow-x-auto overflow-y-auto overscroll-contain relative focus:outline-none"
            // tabIndex={0}
            style={{ overflowAnchor: 'none' }}
            // onScroll={handleScroll}
            onKeyDown={handleKeyDown}
        >
            <table className={`min-w-full text-xs ${scheduleMode ? 'table-fixed' : ''}`}>
                <thead>
                    <tr className="text-left text-gray-500 font-medium">
                        {/* [추가] 변경 컬럼 헤더 */}
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm">
                            <div></div>
                            <div className="text-xs //text-gray-500 font-medium"></div>
                        </th>
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm w-10"></th>
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm min-w-[80px]">
                            <div>Code</div>
                            <div className="text-xs //text-gray-500 font-medium">계층코드</div>
                        </th>
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm //w-[10%] min-w-0">
                            <div
                                className="flex items-center justify-between gap-4 border-r"
                                style={{ width: nameCol.width }}
                            >
                                <div className="px-3">
                                    <div>WBS Name</div>
                                    <div className="text-xs //text-gray-500 font-medium">작업명</div>
                                </div>
                                <div className="text-xs flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="px-2 py-1 mr-4 rounded border hover:bg-gray-50"
                                        onClick={toggleExpandAll}
                                    >
                                        {allExpanded ? '모두 접기' : '모두 펼치기'}
                                    </button>
                                    <div
                                        onMouseDown={nameCol.onMouseDown}
                                        className="ml-auto w-1 self-stretch cursor-col-resize hover:bg-gray-300"
                                        style={{ marginRight: -4 }}
                                        title="열 너비 조절"
                                    />
                                </div>
                            </div>
                        </th>
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm min-w-[90px]">
                            <div>Weight</div>
                            <div className="text-xs //text-gray-500 font-medium">가중치</div>
                        </th>
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm min-w-[110px]">
                            <div>Start</div>
                            <div className="text-xs //text-gray-500 font-medium">작업시작</div>
                        </th>
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm min-w-[110px]">
                            <div>Finish</div>
                            <div className="text-xs //text-gray-500 font-medium">작업끝</div>
                        </th>
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm min-w-[60px]">
                            <div>Days</div>
                            <div className="text-xs //text-gray-500 font-medium">일수</div>
                        </th>
                        {/* 스케줄모드에서 보이는 바차트 */}
                        {scheduleMode && (
                            <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm min-w-0">
                                <div
                                    className="flex items-center gap-4 border-r"
                                    style={{ width: scheduleBarCol.width }}
                                >
                                    <div className="px-3">
                                        <div>Bar Chart</div>
                                        <div className="text-xs //text-gray-500 font-medium">바 차트</div>
                                    </div>
                                    {/* 리사이저 핸들 */}
                                    <div
                                        onMouseDown={scheduleBarCol.onMouseDown}
                                        //   className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-gray-300"
                                        className="ml-auto w-1 self-stretch cursor-col-resize hover:bg-gray-300"
                                        style={{ marginRight: -4 }}
                                        title="열너비 조정"
                                    />
                                </div>
                            </th>
                        )}
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm min-w-[120px]">
                            <div>Assignee</div>
                            <div className="text-xs //text-gray-500 font-medium">작업담당(자)</div>
                        </th>
                        <th className="px-3 py-2 sticky top-0 z-20 bg-white border-b shadow-sm">
                            <div>
                                <div>Memo</div>
                                <div className="text-xs //text-gray-500 font-medium">메모</div>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {visibleRows.map((r) => {
                        if (!r) return null;
                        const id = keyOf(r);
                        const sel = selectedKey === id;
                        const hasChildren = !!treeIndex.childrenMap[r.nodeId!]?.length;
                        const rowExpanded = r.nodeId ? expanded.has(r.nodeId) : false;
                        // const isBusy = busyKey === id;
                        const kindRaw = changeKindMap[id] ?? null;
                        const isPublishedRaw = r.publishedListVersion != null;
                        let kind = kindRaw;
                        if (isPublishedRaw && kindRaw === 'ADDED') {
                            kind = null;
                        }
                        const isDeleted = !!r.isDeleted;
                        const codeToShow = previewOn ? (previewCodeMap[id] ?? r.code ?? '') : (r.code ?? '');

                        const weightWarn = !!r.nodeId && weightWarnSet.has(r.nodeId);
                        const scheduleWarn = !!r.nodeId && scheduleWarnSet.has(r.nodeId);

                        // const start = r.plannedStart ? new Date(r.plannedStart) : null;
                        const start = parseISO(r.plannedStart);
                        // const finish = r.plannedFinish ? new Date(r.plannedFinish) : null;
                        const finish = parseISO(r.plannedFinish);

                        let bar: React.ReactNode = null;

                        if (scheduleMode && scheduleRange && start && finish) {
                            const { min, totalDays } = scheduleRange;

                            const startOffset = Math.max(
                                0,
                                Math.round((start.getTime() - min.getTime()) / (1000 * 60 * 60 * 24)),
                            );
                            const duration = Math.max(
                                1,
                                Math.round((finish.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1,
                            );
                            const leftPercent = (startOffset / totalDays) * 100;
                            const widthPercent = (duration / totalDays) * 100;

                            bar = (
                                <div className="relative h-4 w-full bg-slate-100/70 rounded">
                                    <div
                                        className="absolute h-4 rounded"
                                        style={{
                                            left: `${leftPercent}%`,
                                            width: `${widthPercent}%`,
                                            maxWidth: '100%',
                                            backgroundColor: barColorByLevel(r.depth),
                                        }}
                                    />
                                </div>
                            );
                        }

                        return (
                            <tr
                                ref={(el) => {
                                    const k = String(id);
                                    if (el) rowRefs.current.set(k, el);
                                    else rowRefs.current.delete(k);
                                }}
                                data-row-key={String(id)}
                                key={id}
                                className={`border-t hover:bg-gray-50 hover:cursor-pointer ${
                                    isDeleted ? 'line-through opacity-60' : ''
                                } ${scheduleWarn ? '//bg-rose-50' : ''} ${weightWarn ? '//bg-amber-50' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const y = containerRef.current?.scrollTop ?? null;
                                    savedScrollTopRef.current = y;
                                    try {
                                        if (y != null) sessionStorage.setItem(LS_SCROLL, String(y));
                                    } catch {}
                                    onSelect(id);
                                    containerRef.current?.focus?.({ preventScroll: true } as any);
                                }}
                            >
                                {/* [추가] 변경 컬럼 셀 */}
                                <td className="//px-3 py-2 whitespace-nowrap text-xs">
                                    {kind === 'ADDED' && <span className="text-blue-600">추가</span>}
                                    {kind === 'MODIFIED' && <span className="text-amber-600">수정</span>}
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="radio"
                                        readOnly
                                        checked={sel}
                                        tabIndex={-1}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const y = containerRef.current?.scrollTop ?? null;
                                            savedScrollTopRef.current = y;
                                            try {
                                                if (y != null) sessionStorage.setItem(LS_SCROLL, String(y));
                                            } catch {}
                                            onSelect(id);
                                            containerRef.current?.focus?.({ preventScroll: true } as any);
                                        }}
                                    />
                                </td>
                                <td className="px-3 py-2">{codeToShow}</td>

                                {/* 이름 셀 */}
                                <td className="px-3 py-2">
                                    <div className="flex items-start" style={{ width: nameCol.width }}>
                                        {/* depth 들여쓰기 */}
                                        <div style={{ paddingLeft: `${(r.depth ?? 0) * 16}px` }} />

                                        {/* 아이콘 슬롯 */}
                                        <div className="w-5 h-5 mr-2 inline-flex items-center justify-center">
                                            {hasChildren ? (
                                                <button
                                                    type="button"
                                                    className="mt-0.5 mr-1 inline-flex w-5 h-5 items-center justify-center rounded hover:bg-gray-100"
                                                    title={rowExpanded ? '접기' : '펼치기'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleExpand(r.nodeId ?? undefined);
                                                    }}
                                                >
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 24 24"
                                                        className={`transition-transform ${
                                                            rowExpanded ? 'rotate-90' : ''
                                                        }`}
                                                    >
                                                        <path d="M8 5l8 7-8 7V5z" fill="currentColor" />
                                                    </svg>
                                                </button>
                                            ) : (
                                                <span className="w-5 h-5 mr-1" />
                                            )}
                                        </div>

                                        {/* 텍스트 영역 */}
                                        <div className="relative min-w-0 flex-1">
                                            <span
                                                className="block w-full overflow-hidden whitespace-nowrap text-ellipsis text-left"
                                                title={r.name}
                                            >
                                                {r.name}
                                            </span>
                                            <span
                                                aria-hidden
                                                className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent"
                                            />
                                            <span
                                                aria-hidden
                                                className="pointer-events-none absolute right-1 top-0 h-full flex items-center text-gray-400"
                                            />
                                        </div>
                                    </div>
                                </td>

                                {/* <td className="px-3 py-2 text-right tabular-nums lining-nums">
                                    {r.weight != null ? Number(r.weight).toFixed(4) : '-'}
                                </td> */}
                                <td
                                    className={`px-3 py-2 text-right tabular-nums lining-nums ${weightWarn ? 'text-amber-700 font-semibold' : ''}`}
                                    title={weightWarn && r.nodeId ? (weightTooltipByNode[r.nodeId] ?? '') : undefined}
                                >
                                    {r.weight != null ? Number(r.weight).toFixed(4) : '-'}
                                </td>

                                <td
                                    className={`px-3 py-2 ${scheduleWarn ? 'text-rose-700 font-semibold' : ''}`}
                                    title={
                                        scheduleWarn && r.nodeId ? (scheduleTooltipByNode[r.nodeId] ?? '') : undefined
                                    }
                                >
                                    {r.plannedStart ? toProjectDateOnly(r.plannedStart, zone) : '-'}
                                </td>

                                <td
                                    className={`px-3 py-2 ${scheduleWarn ? 'text-rose-700 font-semibold' : ''}`}
                                    title={
                                        scheduleWarn && r.nodeId ? (scheduleTooltipByNode[r.nodeId] ?? '') : undefined
                                    }
                                >
                                    {r.plannedFinish ? toProjectDateOnly(r.plannedFinish, zone) : '-'}
                                </td>

                                <td
                                    className={`px-3 py-2 text-right tabular-nums lining-nums ${scheduleWarn ? 'text-rose-700 font-semibold' : ''}`}
                                    title={
                                        scheduleWarn && r.nodeId ? (scheduleTooltipByNode[r.nodeId] ?? '') : undefined
                                    }
                                >
                                    {r.durationDays ? r.durationDays : '-'}
                                </td>

                                {scheduleMode && (
                                    <td className="px-3 py-2">
                                        <div>
                                            <div
                                                className="relative h-4 w-full overflow-hidden rounded bg-slate-100/70"
                                                style={{ width: scheduleBarCol.width }}
                                            >
                                                {bar}
                                            </div>
                                        </div>
                                    </td>
                                )}
                                <td className="px-3 py-2">{r.assignee ?? '-'}</td>

                                <td className="px-3 py-2">
                                    <div className="relative min-w-0 flex-1">
                                        <div className="truncate w-[250px]">{r.memo ?? '-'}</div>
                                        <span
                                            aria-hidden
                                            className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent"
                                        />
                                        <span
                                            aria-hidden
                                            className="pointer-events-none absolute right-1 top-0 h-full flex items-center text-gray-400"
                                        />
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
